import * as CL from '@design-system/component-library';
import { useContext, useEffect, useState } from 'react';
import { Link, useLoaderData, useLocation } from 'react-router';
import { pushToDataLayer } from '../../common/analytics';
import { dsClass } from '../../common/constants/dsClasses';
import { paths } from '../../common/constants/pathVariables';
import { createSupportCaseAuthenticated } from '../../common/fetch';
import { t } from '../../common/i18n';
import { getPrimaryMdmId } from '../../common/utils/accountUtils';
import { getKukaSoittaaOrderAnalyticsEvent } from '../../common/utils/analyticsUtils';
import { SupportCase } from '../../generated/api/supportCase';
import { useAuth } from '../../public/site/AuthProvider';
import { SiteContext } from '../../public/site/SiteContext';
import { BreadCrumbs } from '../BreadCrumbs';
import { KukaSoittaaHero } from './KukaSoittaaHero';
import { KukaSoittaaOrderTable } from './KukaSoittaaOrderTable';
import type { SubscriptionsLoaderData } from '../../common/loaders';
import type { SubscriptionHeader } from '../../generated/api/subscriptionHeader';
import type { BreadCrumbList } from '../BreadCrumbs';

interface KukaSoittaaOrderProps {
  breadCrumbs: BreadCrumbList;
}

interface ValuesType {
  selected: string[];
  selectAll: boolean;
}

const ThankYou = () => {
  return (
    <div>
      <div className={`${dsClass.DISPLAY_FLEX} ${dsClass.ALIGN_ITEMS_CENTER} ${dsClass.MARGIN_BOTTOM_3}`}>
        <CL.Icon color="green-600" icon="check" size="xl" type="filled" />
        <h2 className={dsClass.MARGIN_LEFT_2}>{t.O4R6('Thank you for your order!')}</h2>
      </div>
      <p>
        {t.BM5E(
          'We will send a separate confirmation message for each subscription once the service has been activated within 1–2 business days. After this, the service will appear in OmaElisa for Companies for the respective subscription.'
        )}
      </p>
      <div className={dsClass.MARGIN_TOP_6}>
        <Link
          to={paths.SELF_SERVICE_HOME}
          className={`${dsClass.LINK} ${dsClass.LINK_STYLE_LINK_BUTTON} ${dsClass.LINK_BUTTON_COLOR_PRIMARY} ${dsClass.LINK_BUTTON_SIZE_M} ${dsClass.MARGIN_RIGHT_4}`}
        >
          {t.OCYV('Go to the OmaElisa for Companies homepage')}
        </Link>
        <Link
          to={paths.PS_MOBILE_SUBSCRIPTIONS}
          className={`${dsClass.LINK} ${dsClass.LINK_STYLE_LINK_BUTTON} ${dsClass.LINK_BUTTON_COLOR_LIGHT} ${dsClass.LINK_BUTTON_SIZE_M}`}
        >
          {t.HG0J('View your subscriptions')}
        </Link>
      </div>
    </div>
  );
};

const InfoBanner = () => {
  return (
    <CL.Disclaimer
      disclaimerType="info"
      icon={<CL.Icon icon="information" type="regular" aria-hidden="true" />}
      title={t.WTN9('View additional services for your subscriptions')}
      text={t.FPH3(
        'You can view additional services for your subscriptions from the individual subscription page. You can find all your subscriptions on the Products and Services page.'
      )}
      visible
    >
      <div className={`${dsClass.MARGIN_TOP_3}`}>
        <Link to={paths.PS_HOME} className={`${dsClass.LINK}`}>
          {t.I2OG('Go to Products and Services page')}
        </Link>
      </div>
    </CL.Disclaimer>
  );
};

export const KukaSoittaaOrder = ({ breadCrumbs }: KukaSoittaaOrderProps) => {
  const [subHeaders, setSubHeaders] = useState<SubscriptionHeader[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { authenticatedUser } = useAuth();
  const { subscriptions } = useLoaderData() as SubscriptionsLoaderData;
  const PRICE = 2.99;

  const { siteBaseUrl } = useContext(SiteContext);
  const { pathname } = useLocation();

  const caseDescription = (subNumbers: string[]) =>
    `${t.EMSP('Your order for the Elisa Kuka Soittaa Service has been received and is being processed.')} ${t.BM5E(
      'We will send a separate confirmation message for each subscription once the service has been activated within 1–2 business days. After this, the service will appear in OmaElisa for Companies for the respective subscription.'
    )}

    ${t.W0YU('The order applies to the following subscriptions:')} ${subNumbers.join(', ')}`;

  useEffect(() => {
    setSubHeaders(subscriptions.searchResults?.map(sr => sr.result) ?? []);
  }, [subscriptions]);

  const handleSubmit = async (values: ValuesType) => {
    if (loading) {
      return;
    }
    setError(null);
    if (values.selected.length === 0) {
      setError(t.RKRB('Please select at least one subscription.'));
      return;
    }
    const supportCase: SupportCase = {
      feature: SupportCase.FeatureEnum.MASS_ORDER,
      subject: t.TDXL('Order confirmation: Elisa Kuka Soittaa Service'),
      action: t.TDXL('Order confirmation: Elisa Kuka Soittaa Service'),
      url: `${siteBaseUrl}${pathname}`,
      description: caseDescription(values.selected),
    };

    const mdmId = getPrimaryMdmId(authenticatedUser);
    setLoading(true);
    try {
      const response = await createSupportCaseAuthenticated(supportCase, mdmId!);
      pushToDataLayer(getKukaSoittaaOrderAnalyticsEvent(values.selected.length, PRICE, response.supportCaseId!));
      setSubmitted(true);
    } catch (e) {
      setError(t.RVSV('Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <BreadCrumbs breadCrumbPaths={breadCrumbs} />
      <CL.Grid>
        <CL.GridRow justifyCenter>
          <CL.GridCol colWidth={10}>
            <div className="of-kukasoittaa-order">
              {submitted ? (
                <ThankYou />
              ) : (
                <>
                  <KukaSoittaaHero />
                  <KukaSoittaaOrderTable
                    subscriptionHeaders={subHeaders}
                    loading={loading}
                    error={error}
                    setError={setError}
                    handleSubmit={handleSubmit}
                  />
                  <div className={dsClass.MARGIN_TOP_6}>
                    <InfoBanner />
                  </div>
                </>
              )}
            </div>
          </CL.GridCol>
        </CL.GridRow>
      </CL.Grid>
    </>
  );
};
